import { apiService } from './api.service';
import type {
  Embed,
  CreateEmbedDto,
  UpdateEmbedDto,
} from '../../../shared/src/types/embed.types';
import type { Component, Risk, Mitigation } from '@risk-management/shared';

export interface MitigationWithDetails extends Mitigation {
  d3fendName?: string;
}

export interface RiskWithMitigations extends Risk {
  mitigations?: MitigationWithDetails[];
  attackTechniqueName?: string;
}

export interface ComponentWithRisks extends Component {
  risks?: RiskWithMitigations[];
}

export interface EmbedContent {
  embed: Embed;
  content: unknown;
  componentsWithRisks?: ComponentWithRisks[];
}

export const embedsService = {
  /**
   * Get all embeds for a space
   */
  getBySpace: (spaceId: string) =>
    apiService.get<Embed[]>(`/spaces/${spaceId}/embeds`),

  /**
   * Get embed by ID
   */
  getById: (spaceId: string, embedId: string) =>
    apiService.get<Embed>(`/spaces/${spaceId}/embeds/${embedId}`),

  /**
   * Create a new embed for a space
   */
  create: (spaceId: string, data: CreateEmbedDto) =>
    apiService.post<Embed>(`/spaces/${spaceId}/embeds`, data),

  /**
   * Update an embed
   */
  update: (spaceId: string, embedId: string, data: UpdateEmbedDto) =>
    apiService.patch<Embed>(`/spaces/${spaceId}/embeds/${embedId}`, data),

  /**
   * Delete an embed
   */
  delete: (spaceId: string, embedId: string) =>
    apiService.delete<void>(`/spaces/${spaceId}/embeds/${embedId}`),

  /**
   * Get public embed content (no auth required)
   */
  getPublicContent: (token: string) =>
    apiService.get<EmbedContent>(`/embed/${token}?format=json`),

  /**
   * Get embed URL for iframe embedding
   * Returns absolute URL with full domain for use in external systems
   * Uses VITE_PUBLIC_URL if set, otherwise falls back to window.location.origin
   */
  getEmbedUrl: (token: string) => {
    const publicUrl = import.meta.env.VITE_PUBLIC_URL;
    const origin = publicUrl || window.location.origin;
    return `${origin}/embed/${token}`;
  },
};

export default embedsService;
